// src/features/dashboard/context/DashboardDateContext.tsx

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';

import {
  getToday,
  normalizeDate,
  formatApiDate,
} from '../utils/date.utils';


/* ============================================================
   CONTEXT TYPE
============================================================ */

export type DashboardDateContextType = {
  fromDate: Date;
  toDate: Date;

  setFromDate: (
    date: Date,
  ) => void;

  setToDate: (
    date: Date,
  ) => void;
};


/* ============================================================
   CONTEXT
============================================================ */

export const DashboardDateContext =
  createContext<
    DashboardDateContextType | undefined
  >(undefined);


/* ============================================================
   DATE HELPERS

   Exported for other dashboard components if required.
============================================================ */

export const normalizeDashboardDate = (
  date: Date,
): Date => {

  return normalizeDate(
    date,
  );
};


export const formatDashboardDate = (
  date: Date,
): string => {

  return formatApiDate(
    date,
  );
};


/* ============================================================
   PROVIDER
============================================================ */

export const DashboardDateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {

  /* ==========================================================
     INITIAL DATE

     This is evaluated once when the provider is mounted.

     If today is:
       13-Sep-2026

     both values become:
       13-Sep-2026
  ========================================================== */

  const [
    fromDate,
    setFromDateState,
  ] = useState<Date>(
    () => getToday(),
  );

  const [
    toDate,
    setToDateState,
  ] = useState<Date>(
    () => getToday(),
  );


  /* ==========================================================
     SET FROM DATE
  ========================================================== */

  const setFromDate =
    useCallback(
      (date: Date) => {

        const normalizedDate =
          normalizeDashboardDate(
            date,
          );

        console.log(
          'DASHBOARD FROM DATE:',
          normalizedDate,
        );

        console.log(
          'DASHBOARD FROM API DATE:',
          formatDashboardDate(
            normalizedDate,
          ),
        );

        setFromDateState(
          normalizedDate,
        );
      },
      [],
    );


  /* ==========================================================
     SET TO DATE
  ========================================================== */

  const setToDate =
    useCallback(
      (date: Date) => {

        const normalizedDate =
          normalizeDashboardDate(
            date,
          );

        console.log(
          'DASHBOARD TO DATE:',
          normalizedDate,
        );

        console.log(
          'DASHBOARD TO API DATE:',
          formatDashboardDate(
            normalizedDate,
          ),
        );

        setToDateState(
          normalizedDate,
        );
      },
      [],
    );


  /* ==========================================================
     CONTEXT VALUE
  ========================================================== */

  const value =
    useMemo(
      () => ({
        fromDate,
        toDate,
        setFromDate,
        setToDate,
      }),
      [
        fromDate,
        toDate,
        setFromDate,
        setToDate,
      ],
    );


  /* ==========================================================
     PROVIDER
  ========================================================== */

  return (
    <DashboardDateContext.Provider
      value={value}
    >
      {children}
    </DashboardDateContext.Provider>
  );
};


/* ============================================================
   HOOK
============================================================ */

export const useDashboardDate = () => {

  const context =
    useContext(
      DashboardDateContext,
    );

  if (!context) {

    throw new Error(
      'useDashboardDate must be used inside DashboardDateProvider',
    );
  }

  return context;
};